import { LexiWallet } from "@civic/lexi";
import IdentityStoreClient from "./IdentityStoreClient";
import { 
    CredentialClientOptions, 
    CredentialSignerType, 
    SparseCredential, 
    WalletSigner, 
    StoreCredential, 
    ContentItem, 
    CreateStoreRequest,
    ContentItemInternal,
    InternalStoreCredential,
    ContentItemInternalWithStringBlob,
    RecipientKey,
    RecipientKeyType,
  } from "./types";
import { encryptionPackageFromContentItemInternal } from "./utils";
import { EncryptionPackage } from "@civic/lexi/dist/lib/encrypt";
import * as R from "ramda";
import { DEFAULT_STORAGE_PROVIDER } from "./constants";
import { createAnonEncrypter } from 'did-jwt';
import { createAnonDecrypter, decryptJWE } from 'did-jwt';

class CredentialClient {
    private identityStoreClient: IdentityStoreClient;
    private publicSigningString: string;
    private _lexi: LexiWallet | undefined;
    constructor(readonly did: string, readonly options: CredentialClientOptions) {
        if(options.lexi?.publicSigningString) {
            this.publicSigningString = options.lexi.publicSigningString;
        } else {
            const salt = crypto.getRandomValues(new Uint8Array(32));
            this.publicSigningString = Buffer.from(salt).toString('base64');
        }
        
        this.identityStoreClient = new IdentityStoreClient(did, options.auth, options.identityStoreEndpoint);
    }

    public static create(did: string, options: CredentialClientOptions): CredentialClient {
        return new CredentialClient(did, options);
    }

    public static createWithoutDid(options: CredentialClientOptions): CredentialClient {
        return new CredentialClient('', options);
    }

    /**
     * Store a credential by encrypting content is a signer is provided, or 
     * @param request 
     */
    public async store(request: CreateStoreRequest[]): Promise<void> {
        if (this.options.signer?.type === CredentialSignerType.WALLET) {
            // TODO only separate and upload blobs to remote storage if a remote storage-provider is set in the request
            await Promise.all(request.map(req => this.makeLexiStorageRequest(req, this.lexi)));
            return;
        }
        await Promise.all(request.map(req => this.makeUnencryptedStorageRequest(req)));
    }
    
    public addRecipient = async (
        credentialId: string,
        recipientKey: RecipientKey
      ) => {
        // get sparse credential
        const credential = await this.identityStoreClient.getCredential(credentialId)!;
        if(!credential) {
            throw new Error(`Credential ${credentialId} not found!`);
        }

        // add recipient to all content items
        await this.addRecipientToInternalStoreCredential(credential, recipientKey);

        // store sparse credential
        await this.identityStoreClient.patchCredential(credential);
    }

    private addRecipientToInternalStoreCredential = async (
        credential: InternalStoreCredential,
        recipientKey: RecipientKey
      ) => {
        // add recipient to all content items
        await Promise.all(credential?.content.map(async contentItem => {
            const encryptionPackage = encryptionPackageFromContentItemInternal(contentItem);
            await this.addRecipientToEncryptionPackage(encryptionPackage, recipientKey);

            contentItem.encryptionMetadata = JSON.stringify(encryptionPackage);
        }));
    }

    private addRecipientToEncryptionPackage = async (
        encryptionPackage: EncryptionPackage,
        recipientKey: RecipientKey
      ) => {
        // decrypt CEK
        const cek = await this.lexi.decryptCEK(encryptionPackage);
        if (!cek) {
            throw new Error('CEK could not be decrypted');
        }
        
        // encrypt CEK
        let newRecipient;
        if(recipientKey.type === RecipientKeyType.ECDH_ES) {
            const encrypter = createAnonEncrypter(recipientKey.publicKey);
            newRecipient = await encrypter.encryptCek!(cek);
        }
        
        if(!newRecipient) {
            throw new Error('New recipient could not be created');
        }

        // add kid if it is present
        if(recipientKey.kid) {
            newRecipient.header.kid = recipientKey.kid;
        }

        encryptionPackage.payload.recipients?.push(newRecipient!);
    }
    
    private async makeUnencryptedStorageRequest(req: CreateStoreRequest): Promise<void> {
        const blobs = req.content.map(c => Buffer.from(c.blob).toString('base64'));
        const storeCredentialRequest = this.createStoreCredentialRequest(req, req.content.map(R.omit(['blob'])));
        const storedCredentialsResponse = await this.identityStoreClient.storeCredential(storeCredentialRequest);
        const contentWithBlobs = blobs.map((blob, index) => ({ ...storedCredentialsResponse.content[index], stringBlob: blob }));
        await this.uploadBlobsToStorage(contentWithBlobs, req.credentialId);
    }

    private async makeLexiStorageRequest(req: CreateStoreRequest, lexi: LexiWallet): Promise<void> { 
        const encryptedBlobsAsJWEs = await this.encryptBlobsAsJWEs(lexi, req.content);
        const encryptedContentArray = this.createEncryptedContentArray(req.content, encryptedBlobsAsJWEs);
        const storeCredentialRequest = this.createStoreCredentialRequest(req, encryptedContentArray);

        const storedCredentialsResponse = await this.identityStoreClient.storeCredential(storeCredentialRequest);
        const contentWithBlobs = encryptedBlobsAsJWEs.map((encBlob, index) => ({ ...storedCredentialsResponse.content[index], stringBlob: encBlob.payload.ciphertext }));
        await this.uploadBlobsToStorage(contentWithBlobs, req.credentialId);
    }

    private async encryptBlobsAsJWEs(lexi: LexiWallet, content: ContentItem[]): Promise<EncryptionPackage[]> {
        return Promise.all(content.map(async(c) => lexi.encryptForMe(c.blob)));
    }

    private createEncryptedContentArray(content: ContentItem[], encryptedBlobsAsJWEs: EncryptionPackage[]): ContentItemInternal[] {
        return content.map((contentItem, index) => {
            const ciphertextOmitted = { ...encryptedBlobsAsJWEs[index], payload: R.omit(['ciphertext'], encryptedBlobsAsJWEs[index].payload as unknown as Record<string, unknown>) };
            return { ...R.omit(['blob'], contentItem), encryptionMetadata: JSON.stringify(ciphertextOmitted) };
        });
    }

    private createStoreCredentialRequest(request: CreateStoreRequest, contentArray: ContentItemInternal[]): InternalStoreCredential {
        return {
            credentialId: request.credentialId,
            tags: [...new Set([...request.tags, ...request.content.map(c => c.name)])],
            metadata: request.metadata,
            content: contentArray,
            storageProvider: request.storageProvider || DEFAULT_STORAGE_PROVIDER
        };
    }

    private async uploadBlobsToStorage(content: ContentItemInternalWithStringBlob[], credentialId: string): Promise<void> {
        await Promise.all(content.map(async(contentItem) => {
            return this.identityStoreClient.storeDataToStorage(credentialId, contentItem, contentItem.stringBlob);
        }));
    }

    public find(tags?: string[]): Promise<SparseCredential[]> {
        return this.identityStoreClient.findCredentials(tags);
    }

    public async getUnhydrated(id: string): Promise<InternalStoreCredential | null> {
        return this.identityStoreClient.getCredential(id);
    }

    public async get(id: string): Promise<StoreCredential | null> {
        // retrieve the credential
        const retrievedCredential = await this.identityStoreClient.getCredential(id);
        if (!retrievedCredential) {
            return null;
        }

        // if signer is present use it for decryption of the content array
        if (this.options.signer) {
            const decryptedContentArray = await this.decryptContentArray(retrievedCredential.content);
            return { ...retrievedCredential, content: decryptedContentArray };
        }
        // otherwise return undecrypted content array
        return { ...retrievedCredential, content: await this.retrieveContentArray(retrievedCredential.content) };
    }

    private async decryptContentArray(content: ContentItemInternal[]): Promise<ContentItem[]> {
        return Promise.all(content.map(async(contentItem: ContentItemInternal): Promise<ContentItem> => {
            // preconditions
            if(contentItem.format !== 'JWE') {
                return contentItem as ContentItem;
            }
            if(!contentItem.encryptionMetadata) {
                throw new Error('JWE content format but no encryption metadata!');
            }
            if(!contentItem.ref) {
                throw new Error('JWE content format without ref!');
            }

            // get and hydrate encryption package
            const encryptionPackage = encryptionPackageFromContentItemInternal(contentItem);
            const ciphertext = await this.identityStoreClient.getDataFromStorage(contentItem);
            if (!ciphertext) {
                throw new Error('No ciphertext found in storage!');
            }
            encryptionPackage.payload.ciphertext = ciphertext;

            // decrypt the blob
            const decryptedBlob = await this.decryptBlob(encryptionPackage);

            // return content item with decrypted blob
            return { ...R.omit(["encryptionMetadata", "presignedUrl", "ref"], contentItem), blob: decryptedBlob };
        }));
    }

    private async decryptBlob(encryptionPackage: EncryptionPackage): Promise<Uint8Array> {
        // if ECDH-ES recipient key is provided use it for decryption, otherwise use lexi
        if(this.options.signer?.type === CredentialSignerType.ECDH_ES) {
            const decrypter = createAnonDecrypter(this.options.signer.secretKey);
            return decryptJWE(encryptionPackage.payload, decrypter);
        } else if(this.options.signer?.type === CredentialSignerType.WALLET) {
            return this.lexi.decrypt(encryptionPackage);
        }
        
        throw new Error('Unsupported signer type!');
    }

    private async retrieveContentArray(content: ContentItemInternal[]): Promise<ContentItem[]> {
        return Promise.all(content.map(async(contentItem: ContentItemInternal): Promise<ContentItem> => {
            const downloadedContent = await this.identityStoreClient.getDataFromStorage(contentItem);
            if (!downloadedContent) {   
                throw new Error('No content found in storage!');
            }
            return { ...R.omit(["encryptionMetadata", "presignedUrl", "ref"], contentItem), blob: Buffer.from(downloadedContent, 'base64') };
        }));
        
    }

    async delete(id: string): Promise<void> {
        await this.identityStoreClient.deleteCredential(id);
    }

    public get lexi(): LexiWallet {
        if (!this._lexi) {
            this._lexi = new LexiWallet(this.options.signer as WalletSigner, this.did, { publicSigningString: this.publicSigningString, ...(this.options.lexi || {}) });
        }
        return this._lexi;
    }
}

export default CredentialClient;
